import React from 'react';
import { ChevronDown, ChevronRight, Check, X } from 'lucide-react';
import Badge from '../ui/Badge';
import ReconciliationStrategyBreakdown from './ReconciliationStrategyBreakdown';
import { formatPnlParts } from '../../utils/positionDisplay';
import { CHIP_PILL } from '../../utils/chipColors';
import type { ReconciliationInstrument } from '../../types/reconciliation';

// "Pending sync" badge — Family B page-level pill (amber attention). Sourced
// from canonical CHIP_PILL.ATTENTION. Pre-Phase-0 used `border` instead of
// `ring`; switching to ring matches every other Family B pill in the app and
// frees the 1px from layout (chips no longer consume box width for the edge).
const PENDING_SYNC_CHIP =
  `inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium ${CHIP_PILL.ATTENTION}`;

interface Props {
  instruments: ReconciliationInstrument[];
}

const formatSignedQty = (q: number): string => {
  if (q > 0) return `+${q}`;
  if (q < 0) return `${q}`;
  return '0';
};

const qtyColor = (q: number): string => {
  if (q > 0) return 'text-green-600 dark:text-green-400';
  if (q < 0) return 'text-red-500 dark:text-red-400';
  return 'text-gray-700 dark:text-gray-300';
};

const ReconciliationTable: React.FC<Props> = ({ instruments }) => {
  const [expanded, setExpanded] = React.useState<Set<string>>(new Set());

  if (!instruments.length) {
    return (
      <div className="px-6 py-12 text-center text-sm text-gray-500 dark:text-gray-400">
        No instruments to reconcile.
      </div>
    );
  }

  const toggle = (key: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const keyOf = (i: ReconciliationInstrument) => `${i.symbol}|${i.exchange}|${i.product}`;

  return (
    <>
      {/* Mobile — card list. The 9-column table is unusable below ~640px;
          give each instrument a dedicated card with stacked rows so all the
          key data fits without horizontal scroll. */}
      <div className="block sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
        {instruments.map(i => {
          const key = keyOf(i);
          const isOpen = expanded.has(key);
          const pnl = formatPnlParts(i.brokerTotalPnl);
          return (
            <React.Fragment key={key}>
              <div
                onClick={() => toggle(key)}
                className="px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                {/* Row 1: chevron + symbol + match icon (+ optional pending sync) */}
                <div className="flex items-center gap-2">
                  {isOpen ? (
                    <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400 shrink-0" />
                  )}
                  <span className="flex-1 min-w-0 text-sm font-mono font-semibold text-gray-900 dark:text-white truncate">
                    {i.symbol}
                  </span>
                  {i.match ? (
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400 shrink-0"
                      title="Quantities match"
                    >
                      <Check className="w-3.5 h-3.5" />
                    </span>
                  ) : (
                    <span
                      className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400 shrink-0"
                      title={i.sideMismatch ? 'Side mismatch — broker and platform on opposite sides' : 'Quantity mismatch'}
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>

                {/* Row 2: meta — exchange · product · pending-sync */}
                <div className="ml-6 mt-1 flex items-center flex-wrap gap-1.5 text-xs">
                  <span className="text-gray-500 dark:text-gray-400">{i.exchange}</span>
                  <span className="text-gray-300 dark:text-gray-600">·</span>
                  <Badge variant="info" size="sm">{i.product || '—'}</Badge>
                  {i.hasPendingSync && (
                    <span
                      className={PENDING_SYNC_CHIP}
                      title="At least one position is EXIT_PENDING — broker_orders_sync_handler will reconcile this within ~60 seconds"
                    >
                      Pending sync
                    </span>
                  )}
                </div>

                {/* Row 3: 2x2 stat grid */}
                <div className="ml-6 mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
                  <Stat label="Broker">
                    <span className={`tabular-nums font-semibold ${qtyColor(i.brokerNetQty)}`}>
                      {formatSignedQty(i.brokerNetQty)}
                    </span>
                  </Stat>
                  <Stat label="Platform">
                    <span className={`tabular-nums font-semibold ${qtyColor(i.platformNetQty)}`}>
                      {formatSignedQty(i.platformNetQty)}
                    </span>
                  </Stat>
                  <Stat label="Variance">
                    {i.variance === 0 ? (
                      <span className="text-gray-500 dark:text-gray-400 tabular-nums">0</span>
                    ) : (
                      <Badge variant={i.sideMismatch ? 'danger' : 'warning'} size="sm">
                        <span className="tabular-nums">{formatSignedQty(i.variance)}</span>
                      </Badge>
                    )}
                  </Stat>
                  <Stat label="P&L">
                    <span
                      className={`tabular-nums font-semibold ${pnl.className}`}
                      title={
                        i.brokerTotalPnl !== null && i.brokerTotalPnl !== undefined
                          ? `Realized ${formatPnlParts(i.brokerRealizedPnl).text} · Unrealized ${formatPnlParts(i.brokerUnrealizedPnl).text}`
                          : undefined
                      }
                    >
                      {pnl.text}
                    </span>
                  </Stat>
                </div>
              </div>
              {isOpen && (
                <div className="bg-gray-50 dark:bg-gray-900/40">
                  <ReconciliationStrategyBreakdown strategies={i.strategies} />
                </div>
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Desktop — full 9-column table */}
      <div className="hidden sm:block overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-3 py-3 w-8" aria-hidden="true"></th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Symbol</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Exchange</th>
              <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Product</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Broker Qty</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Platform Qty</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Variance</th>
              <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300" title="Broker-reported realized + unrealized P&amp;L for this instrument">P&amp;L</th>
              <th className="px-3 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-300">Match</th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
            {instruments.map(i => {
              const key = keyOf(i);
              const isOpen = expanded.has(key);
              return (
                <React.Fragment key={key}>
                  <tr
                    onClick={() => toggle(key)}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer"
                  >
                    <td className="px-3 py-4 align-middle">
                      {isOpen ? (
                        <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm font-mono text-gray-900 dark:text-white whitespace-nowrap">
                      {i.symbol}
                    </td>
                    <td className="px-3 py-4 text-xs text-gray-600 dark:text-gray-300">
                      {i.exchange}
                    </td>
                    <td className="px-3 py-4 text-xs">
                      <Badge variant="info" size="sm">{i.product || '—'}</Badge>
                    </td>
                    <td className={`px-3 py-4 text-sm text-right tabular-nums font-semibold ${qtyColor(i.brokerNetQty)}`}>
                      {formatSignedQty(i.brokerNetQty)}
                    </td>
                    <td className={`px-3 py-4 text-sm text-right tabular-nums font-semibold ${qtyColor(i.platformNetQty)}`}>
                      {formatSignedQty(i.platformNetQty)}
                    </td>
                    <td className="px-3 py-4 text-sm text-right">
                      {i.variance === 0 ? (
                        <span className="text-gray-500 dark:text-gray-400 tabular-nums">0</span>
                      ) : (
                        <Badge variant={i.sideMismatch ? 'danger' : 'warning'} size="sm">
                          <span className="tabular-nums">{formatSignedQty(i.variance)}</span>
                        </Badge>
                      )}
                    </td>
                    <td className="px-3 py-4 text-sm text-right">
                      {(() => {
                        const { text, className } = formatPnlParts(i.brokerTotalPnl);
                        const tooltip = i.brokerTotalPnl !== null && i.brokerTotalPnl !== undefined
                          ? `Realized ${formatPnlParts(i.brokerRealizedPnl).text} · Unrealized ${formatPnlParts(i.brokerUnrealizedPnl).text}`
                          : undefined;
                        return (
                          <span className={`tabular-nums font-semibold ${className}`} title={tooltip}>
                            {text}
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-3 py-4 text-center">
                      <div className="flex items-center justify-center gap-1.5">
                        {i.match ? (
                          <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400" title="Quantities match">
                            <Check className="w-4 h-4" />
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-red-50 dark:bg-red-900/20 text-red-500 dark:text-red-400"
                            title={i.sideMismatch ? 'Side mismatch — broker and platform on opposite sides' : 'Quantity mismatch'}
                          >
                            <X className="w-4 h-4" />
                          </span>
                        )}
                        {i.hasPendingSync && (
                          <span
                            className={PENDING_SYNC_CHIP}
                            title="At least one position is EXIT_PENDING — broker_orders_sync_handler will reconcile this within ~60 seconds"
                          >
                            Pending sync
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={`${key}-detail`} className="bg-gray-50 dark:bg-gray-900/40">
                      <td colSpan={9} className="p-0">
                        <ReconciliationStrategyBreakdown strategies={i.strategies} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
};

const Stat: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div className="flex items-baseline gap-1.5">
    <span className="text-gray-500 dark:text-gray-400 uppercase tracking-wider text-[10px]">{label}</span>
    <span className="ml-auto">{children}</span>
  </div>
);

export default ReconciliationTable;
